import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import {
  useAuth,
  useLogout,
  useRefreshToken,
  getAccessToken,
  clearTokens,
  type User,
} from '@theraply/api-client';

interface AuthContextType {
  user: User | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: { message: string } | null;
  login: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isAuthenticated, isLoading, error, refetch } = useAuth();
  const { mutateAsync: logoutMutation } = useLogout();
  const { mutateAsync: refreshMutation } = useRefreshToken();

  // Set up automatic token refresh
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    // Decode JWT to get expiration time
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Refresh 1 minute before expiry
      const refreshTime = timeUntilExpiry - 60 * 1000;

      if (refreshTime > 0) {
        const timeoutId = setTimeout(() => {
          refreshMutation().catch(() => {
            // Token refresh failed, clear tokens
            clearTokens();
          });
        }, refreshTime);

        return () => clearTimeout(timeoutId);
      } else if (timeUntilExpiry < 0) {
        // Token already expired
        clearTokens();
      }
    } catch {
      // Invalid token format
      clearTokens();
    }
  }, [refreshMutation, user]);

  const login = useCallback(() => {
    refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation();
  }, [logoutMutation]);

  const refreshSession = useCallback(async () => {
    await refreshMutation();
    refetch();
  }, [refreshMutation, refetch]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
